/**
 * Wrapper sobre fetch para el API.
 *
 * - Siempre envía cookies httpOnly (`credentials: 'include'`).
 * - Base URL: en producción same-origin (vía nginx /api/*), en dev Vite proxy.
 * - Parsea JSON, lanza ApiError si status >= 400.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { error?: string; message?: string; issues?: Record<string, string[]> },
  ) {
    super(body.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

const BASE = ''; // same-origin (nginx /api o vite proxy)

interface RequestOptions extends Omit<RequestInit, 'body'> {
  json?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(BASE + path, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const init: RequestInit = {
    ...opts,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(opts.json ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.headers ?? {}),
    },
    body: opts.json !== undefined ? JSON.stringify(opts.json) : (opts as RequestInit).body,
  };

  const res = await fetch(url.toString(), init);

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    throw new ApiError(res.status, (body as ApiError['body']) ?? { message: res.statusText });
  }

  return body as T;
}

export const apiGet = <T = unknown>(path: string, params?: RequestOptions['params']) =>
  api<T>(path, { method: 'GET', params });

export const apiPost = <T = unknown>(path: string, json?: unknown) =>
  api<T>(path, { method: 'POST', json });

export const apiPatch = <T = unknown>(path: string, json?: unknown) =>
  api<T>(path, { method: 'PATCH', json });

export const apiPut = <T = unknown>(path: string, json?: unknown) =>
  api<T>(path, { method: 'PUT', json });

export const apiDelete = <T = unknown>(path: string) =>
  api<T>(path, { method: 'DELETE' });
